import React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { FileUpload, FileUploadNegative } from '@surglogs-app/icons'
import { Box, FlexBox, Icon, Text, TextBold } from '@surglogs-app/ui'

import { useTranslation } from '../services'
import { FileInputBaseProps } from '../types'

type FileInputContainerProps = Pick<
  FileInputBaseProps,
  'invalid' | 'warning' | 'dragging' | 'dragAndDropArea' | 'disabled'
>

const getBorderColor = ({
  warning,
  invalid,
  disabled,
}: FileInputContainerProps) => {
  if (disabled) {
    return 'navy.10'
  }

  if (warning) {
    return 'orange.100'
  }

  if (invalid) {
    return 'red.100'
  }

  return 'navy.25'
}

const StyledDragAndDropArea = styled(Box)<FileInputContainerProps>`
  border: 1px solid
    ${({ theme, warning, invalid, disabled }) =>
      theme.ui.colors[getBorderColor({ warning, invalid, disabled })]};

  border-radius: ${({ theme }) => theme.ui.spacings.sm};
  padding: ${({ theme }) => theme.ui.spacings.lg};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ theme, disabled }) =>
    theme.ui.colors[disabled ? 'navy.25' : 'navy.100']};
  background-color: ${({ theme, disabled }) =>
    theme.ui.colors[disabled ? 'navy.10' : 'transparent']};

  ${({ theme, dragging }) =>
    dragging
      ? css`
          border-color: ${theme.ui.colors['blue.100']};
          background-color: ${theme.ui.colors['blue.100']};
          color: ${theme.ui.colors['white.100']};
        `
      : css`
          &:hover {
            border-color: ${theme.ui.colors['blue.100']};
            color: ${theme.ui.colors['blue.100']};
          }
        `};
`

const StyledLabel = styled.label<{ disabled?: boolean }>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`

const StyledTextContainer = styled(Box)`
  text-align: center;
  user-select: none;
  pointer-events: none;
`

type PassedPropsToInput = Pick<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'onChange' | 'onDrop' | 'id' | 'accept' | 'multiple'
>

type Props = FileInputContainerProps & PassedPropsToInput

export const DragAndDropArea = ({
  onChange,
  onDrop,
  id,
  accept,
  multiple,
  disabled,
  ...props
}: Props) => {
  const { t } = useTranslation()

  return (
    <StyledLabel {...{ htmlFor: id, disabled }}>
      <StyledDragAndDropArea
        {...{
          onDrop,
          disabled,
          role: 'group',
          'aria-label': 'File dropzone',
          ...props,
        }}>
        <input
          {...{
            accept,
            id,
            type: 'file',
            onChange,
            onDrop,
            hidden: true,
            multiple,
          }}
        />
        <StyledTextContainer>
          {props.dragAndDropArea ? (
            props.dragAndDropArea
          ) : (
            <FlexBox {...{ ai: 'center', gap: 'md' }}>
              <Icon
                {...{
                  color: 'inherit',
                  icon: props.dragging ? FileUploadNegative : FileUpload,
                }}
              />
              <FlexBox
                {...{
                  fd: 'column',
                  jc: 'flex-start',
                  ta: 'left',
                  gap: 'xs',
                }}>
                <TextBold {...{ color: 'inherit' }}>
                  {t('dragAndDrop')}
                </TextBold>
                <Text {...{ size: 'xxs', color: 'inherit' }}>
                  {t('dragAndDropText')}
                </Text>
              </FlexBox>
            </FlexBox>
          )}
        </StyledTextContainer>
      </StyledDragAndDropArea>
    </StyledLabel>
  )
}
